import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFacility } from "@/context/FacilityContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { mdy } from "@/lib/dateFmt";
import {
  CalendarClock, Plus, Trash2, Wand2, AlertTriangle, Check,
  Users2, X, UserPlus, GripVertical, Printer, MapPin, ShieldCheck,
} from "lucide-react";

// Meeting Manager. Admin-only tab that lets you plan a multi-session meeting,
// pick a roster, and run the auto-assign optimizer. Layout is row-paired:
// each attendee sits next to their coverer, plus a shared "Available
// Coverers" pool at the bottom for drag-drop assignment. Admins can manually
// add PDs/off-benefitted employees who agreed to help cover.
export default function MeetingsPage() {
  const { user } = useAuth();
  const { facilityId } = useFacility();
  const isAdmin = user?.role === "admin";
  const [meetings, setMeetings] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [loading, setLoading] = useState(false);

  // ── Auto-scroll while dragging ──────────────────────────────────────────
  // HTML5 drag-and-drop does NOT auto-scroll the viewport, so a user
  // dragging a tile from the bottom of the page toward the top gets stuck.
  // We arm a global dragover listener on `dragstart` that scrolls the
  // window when the pointer approaches the top or bottom edge, and disarm
  // it on `dragend`/`drop`. Speed scales with how close the pointer is to
  // the edge, capped at a comfortable ~24 px per frame.
  useEffect(() => {
    const EDGE = 90;   // px from viewport top/bottom that trigger scrolling
    const MAX_STEP = 24;
    let raf = 0;
    let dy = 0;
    const step = () => {
      if (dy !== 0) window.scrollBy(0, dy);
      raf = dy !== 0 ? requestAnimationFrame(step) : 0;
    };
    const onDragOver = (e) => {
      const y = e.clientY;
      const h = window.innerHeight;
      if (y < EDGE)      dy = -Math.ceil(((EDGE - y) / EDGE) * MAX_STEP);
      else if (y > h - EDGE) dy =  Math.ceil(((y - (h - EDGE)) / EDGE) * MAX_STEP);
      else               dy = 0;
      if (dy !== 0 && !raf) raf = requestAnimationFrame(step);
    };
    const stop = () => { dy = 0; if (raf) cancelAnimationFrame(raf); raf = 0; };
    const onDragStart = () => {
      window.addEventListener("dragover", onDragOver, { passive: true });
      window.addEventListener("dragend", stop);
      window.addEventListener("drop", stop);
    };
    const onDragEnd = () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragend", stop);
      window.removeEventListener("drop", stop);
      stop();
    };
    window.addEventListener("dragstart", onDragStart);
    window.addEventListener("dragend", onDragEnd);
    window.addEventListener("drop", onDragEnd);
    return () => {
      window.removeEventListener("dragstart", onDragStart);
      window.removeEventListener("dragend", onDragEnd);
      window.removeEventListener("drop", onDragEnd);
      stop();
    };
  }, []);

  const loadList = useCallback(async () => {
    if (!facilityId) return;
    try {
      const { data } = await api.get(`/meetings?facility_id=${facilityId}&status=all`);
      setMeetings(data || []);
      if (data && data.length && !selectedId) setSelectedId(data[0].id);
    } catch (e) {
      toast.error("Failed to load meetings");
    }
  }, [facilityId, selectedId]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { setSelectedId(null); }, [facilityId]);

  const selected = useMemo(
    () => meetings.find((m) => m.id === selectedId) || null,
    [meetings, selectedId],
  );

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-slate-500">
        Meeting Manager is admin-only.
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest font-black text-slate-500">Admin only</div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarClock size={22} className="text-fuchsia-600" /> Meeting Manager
          </h1>
          <div className="text-sm text-slate-500 mt-0.5">
            Create a multi-session meeting, auto-assign attendees + coverage,
            then fine-tune with drag-and-drop.
          </div>
        </div>
        <Button
          onClick={() => setOpenNew(true)}
          className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
          data-testid="btn-new-meeting"
        >
          <Plus size={14} className="mr-1.5" /> New meeting
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-4">
        <MeetingList
          meetings={meetings}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        {selected ? (
          <MeetingDetail
            meeting={selected}
            onChange={loadList}
            loading={loading}
            setLoading={setLoading}
          />
        ) : (
          <Card className="p-8 text-center text-sm text-slate-500">
            {meetings.length === 0
              ? "No meetings yet. Click New Meeting to create one."
              : "Pick a meeting on the left to see its roster."}
          </Card>
        )}
      </div>

      {openNew && (
        <NewMeetingDialog
          facilityId={facilityId}
          onClose={() => setOpenNew(false)}
          onCreated={(m) => { setOpenNew(false); setSelectedId(m.id); loadList(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function MeetingList({ meetings, selectedId, onSelect }) {
  const statusColor = {
    draft: "bg-amber-100 text-amber-800 border-amber-300",
    finalized: "bg-emerald-100 text-emerald-800 border-emerald-300",
    cancelled: "bg-slate-100 text-slate-500 border-slate-300",
  };
  return (
    <Card className="p-0 overflow-hidden">
      {meetings.length === 0 && (
        <div className="p-6 text-center text-sm text-slate-500">
          No meetings yet.
        </div>
      )}
      <div className="divide-y divide-slate-100">
        {meetings.map((m) => {
          const dates = [...new Set((m.sessions || []).map(s => s.date))].sort();
          const first = dates[0], last = dates[dates.length - 1];
          return (
            <button
              key={m.id}
              onClick={() => onSelect(m.id)}
              data-testid={`meeting-row-${m.id}`}
              className={`w-full text-left p-3 hover:bg-slate-50 transition-colors ${
                selectedId === m.id ? "bg-fuchsia-50" : ""
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-slate-900 truncate">{m.name}</div>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${statusColor[m.status] || ""}`}>
                  {m.status}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {first === last ? mdy(first) : `${mdy(first)} – ${mdy(last)}`} · {(m.sessions || []).length} session{(m.sessions || []).length === 1 ? "" : "s"}
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Drag payload shape. Encoded as JSON in dataTransfer so the drop handler can
// tell WHERE the tile came from (pool, an existing cover slot, or an attend
// row for reassignment).
//   { kind: "attend", assignment_id }             — dragging an attendee row between sessions
//   { kind: "cover_pool", employee_id }           — dragging from pool onto a cover slot
//   { kind: "cover_slot", assignment_id, employee_id, session_id }
//                                                   — dragging an existing cover row
// -----------------------------------------------------------------------------

function MeetingDetail({ meeting, onChange, loading, setLoading }) {
  const { facilityId } = useFacility();
  const [emps, setEmps] = useState([]);
  const [openAddCoverer, setOpenAddCoverer] = useState(false);
  const [poolDragHover, setPoolDragHover] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/roster?facility_id=${facilityId}`).then(({ data }) => {
      if (alive) setEmps(data || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, [facilityId]);

  const empById = useMemo(() => {
    const m = {}; for (const e of emps) m[e.id] = e; return m;
  }, [emps]);

  const runAutoAssign = async () => {
    setLoading(true);
    try {
      await api.post(`/meetings/${meeting.id}/auto-assign`);
      toast.success("Auto-assign complete");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Auto-assign failed");
    }
    setLoading(false);
  };

  // Replace the full assignments array on the meeting. Used by every drag-drop
  // operation so the backend can re-check OT and rebuild warnings.
  const saveAssignments = async (nextAssignments) => {
    try {
      await api.patch(`/meetings/${meeting.id}/assignments`, {
        // Strip the frontend-only `cover_needed` before sending.
        assignments: nextAssignments.map(({ cover_needed, ...rest }) => rest),
      });
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
      onChange();  // reload so UI reflects true server state
    }
  };

  // ── Drag handlers ─────────────────────────────────────────────────────────

  // Attend row dragged onto another session card — move it to that session.
  const moveAttendTo = (assignmentId, targetSessId) => {
    const next = (meeting.assignments || []).map((a) => {
      if (a.id === assignmentId && a.role === "attend") {
        return { ...a, session_id: targetSessId, manual: true };
      }
      return a;
    });
    saveAssignments(next);
  };

  // Pool member dropped onto an empty cover slot for a specific attendee.
  // Adds a new cover row; if that pool member already has a cover row on
  // another slot in the same session, move it (they can only cover one).
  const dropPoolMemberToCover = (employeeId, targetSessId, attendeeId, attendeeDept) => {
    // Remove any existing cover row for this employee in this session
    // (they can only be one place at a time).
    const next = (meeting.assignments || []).filter(
      (a) => !(a.role === "cover" && a.employee_id === employeeId && a.session_id === targetSessId)
    );
    next.push({
      id: `asn-new-${Math.random().toString(36).slice(2, 10)}`,
      employee_id: employeeId,
      session_id: targetSessId,
      role: "cover",
      covers_employee_id: attendeeId,
      attendee_dept: attendeeDept || null,
      mode: "manual",
      manual: true,
    });
    saveAssignments(next);
  };

  // Existing cover row moved to a different attendee (possibly different
  // session). Passes both the row id AND the new target to re-key.
  const moveCoverRow = (assignmentId, newSessId, newAttendeeId, newAttendeeDept) => {
    const next = (meeting.assignments || []).map((a) => {
      if (a.id === assignmentId && a.role === "cover") {
        return {
          ...a,
          session_id: newSessId,
          covers_employee_id: newAttendeeId,
          attendee_dept: newAttendeeDept || null,
          manual: true,
        };
      }
      return a;
    });
    saveAssignments(next);
  };

  // Cover row removed (dropped onto the pool zone).
  const removeCoverRow = (assignmentId) => {
    const next = (meeting.assignments || []).filter((a) => a.id !== assignmentId);
    saveAssignments(next);
  };

  const removeFromPool = async (employeeId) => {
    if (!window.confirm("Remove this coverer from the pool? Any cover slots they're on will also clear.")) return;
    try {
      await api.delete(`/meetings/${meeting.id}/cover-pool/${employeeId}`);
      toast.success("Removed from pool");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Remove failed");
    }
  };

  const cancelMeeting = async () => {
    if (!window.confirm(`Cancel "${meeting.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/meetings/${meeting.id}`);
      toast.success("Meeting cancelled");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to cancel");
    }
  };

  const finalize = async () => {
    if (!window.confirm("Finalize this meeting? Schedule notes will be written to everyone's calendars.")) return;
    setLoading(true);
    try {
      await api.post(`/meetings/${meeting.id}/finalize`);
      toast.success("Meeting finalized — schedule notes posted");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to finalize");
    }
    setLoading(false);
  };

  const unfinalize = async () => {
    if (!window.confirm("Unfinalize this meeting? Schedule notes will be removed and you can edit again.")) return;
    setLoading(true);
    try {
      await api.post(`/meetings/${meeting.id}/unfinalize`);
      toast.success("Reverted to draft — schedule notes cleared");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to unfinalize");
    }
    setLoading(false);
  };

  // Two-step demotion so the admin can reconsider before permanently
  // excluding someone:
  //   1. X on an attend tile   → removeAttend  → lands in "Needs self-signup"
  //   2. X on unassignable row → excludeEmployee → lands in "Not required"
  //   3. Restore                                → back to "Needs self-signup"
  const removeAttend = async (employeeId, name) => {
    if (!window.confirm(`Move ${name || "this employee"} to the "Needs self-signup" list?`)) return;
    try {
      await api.post(`/meetings/${meeting.id}/remove-attend`, { employee_id: employeeId });
      toast.success("Moved to needs self-signup");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to remove");
    }
  };

  const excludeEmployee = async (employeeId, name) => {
    if (!window.confirm(`${name || "This employee"} won't be required to attend. Confirm?`)) return;
    try {
      await api.post(`/meetings/${meeting.id}/exclude`, { employee_id: employeeId });
      toast.success("Marked not required");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to exclude");
    }
  };

  const restoreEmployee = async (employeeId) => {
    try {
      await api.delete(`/meetings/${meeting.id}/exclude/${employeeId}`);
      toast.success("Restored — back on needs self-signup list");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to restore");
    }
  };

  // Drag an unassignable employee onto a session card → attend that session.
  // We look up the employee's scheduled shift on that date via the roster so
  // we can set the attendee_dept (if they have one). If no scheduled shift
  // matches, they attend as a manual (no cover needed).
  const dropUnassignableToSession = async (employeeId, targetSessId) => {
    const next = [...(meeting.assignments || []),
      {
        id: `asn-new-${Math.random().toString(36).slice(2, 10)}`,
        employee_id: employeeId,
        session_id: targetSessId,
        role: "attend",
        mode: "manual_attend",
        attendee_dept: null,      // admin-dragged → no forced coverage
        covers_employee_id: null,
        manual: true,
      },
    ];
    saveAssignments(next);
  };

  const hardDelete = async () => {
    if (!window.confirm(`Permanently delete "${meeting.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/meetings/${meeting.id}/permanent`);
      toast.success("Meeting deleted");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to delete");
    }
  };

  // Toggle the "no cover needed" flag on an attend row. Used for depts that
  // can walk away (PFA, cashier) or admin overrides. Training shifts are
  // auto-flagged by the backend.
  const toggleNoCover = (assignmentId) => {
    const next = (meeting.assignments || []).map((a) =>
      a.id === assignmentId && a.role === "attend"
        ? { ...a, no_cover_needed: !a.no_cover_needed }
        : a
    );
    saveAssignments(next);
  };

  // Inline edit of a session's location (renders "TBD" when blank).
  const setSessionLocation = async (sessionId, currentLocation) => {
    const next = window.prompt("Location for this session (leave blank for TBD):",
                                currentLocation || "");
    if (next === null) return;   // cancelled
    try {
      await api.post(`/meetings/${meeting.id}/session/${sessionId}/location`,
                     { location: next });
      toast.success("Location saved");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save");
    }
  };

  // Download the roster PDF (works for draft + finalized).
  const printRoster = async () => {
    try {
      const resp = await api.get(`/meetings/${meeting.id}/roster-pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Failed to generate PDF");
    }
  };

  const sendEmails = async () => {
    if (!window.confirm(`Email a self-signup link to the ${(meeting.unassignable || []).length} unassignable employees?`)) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/send-signup-emails`);
      toast.success(`Emails queued: ${data.queued} · Failed: ${data.failed} · Already signed up: ${data.already_signed_up}`);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to send");
    }
    setLoading(false);
  };

  // Group assignments per session — attend + cover mapped together.
  const bySession = useMemo(() => {
    const m = {};
    for (const s of meeting.sessions || []) {
      m[s.id] = { attend: [], coverByAttendee: {}, session: s };
    }
    for (const a of meeting.assignments || []) {
      const bucket = m[a.session_id];
      if (!bucket) continue;
      if (a.role === "attend") bucket.attend.push(a);
      else if (a.role === "cover" && a.covers_employee_id) {
        bucket.coverByAttendee[a.covers_employee_id] = a;
      }
    }
    return m;
  }, [meeting]);

  const hasAssignments = (meeting.assignments || []).length > 0;
  const isDraft = meeting.status === "draft";

  return (
    <div className="space-y-4">
      {/* Header + actions */}
      <Card className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xl font-bold text-slate-900">{meeting.name}</div>
            {meeting.description && (
              <div className="text-sm text-slate-600 mt-1">{meeting.description}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={printRoster}
              variant="outline"
              className="border-slate-300"
              data-testid="btn-print-roster"
            >
              <Printer size={14} className="mr-1.5" /> Print roster
            </Button>
            {isDraft && (
              <>
                <Button
                  onClick={runAutoAssign}
                  disabled={loading}
                  className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                  data-testid="btn-auto-assign"
                >
                  <Wand2 size={14} className="mr-1.5" />
                  {loading ? "Assigning…" : hasAssignments ? "Re-run auto-assign" : "Run auto-assign"}
                </Button>
                {hasAssignments && (
                  <Button
                    onClick={finalize}
                    disabled={loading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="btn-finalize"
                  >
                    <Check size={14} className="mr-1.5" /> Finalize
                  </Button>
                )}
                <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={cancelMeeting}>
                  <Trash2 size={14} className="mr-1.5" /> Cancel
                </Button>
              </>
            )}
            {meeting.status === "cancelled" && (
              <>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border bg-slate-100 border-slate-300 text-slate-600 text-xs font-bold uppercase tracking-widest">
                  Cancelled
                </span>
                <Button
                  onClick={hardDelete}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="btn-hard-delete"
                >
                  <Trash2 size={14} className="mr-1.5" /> Delete permanently
                </Button>
              </>
            )}
            {meeting.status === "finalized" && (
              <>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border bg-emerald-50 border-emerald-300 text-emerald-800 text-xs font-bold uppercase tracking-widest">
                  <Check size={12} /> Finalized
                </span>
                <Button
                  onClick={unfinalize}
                  disabled={loading}
                  variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="btn-unfinalize"
                >
                  Unfinalize
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Warnings */}
      {(meeting.warnings || []).length > 0 && (
        <Card className="p-4 border-2 border-amber-300 bg-amber-50">
          <div className="text-[11px] uppercase tracking-widest font-black text-amber-900 flex items-center gap-1 mb-2">
            <AlertTriangle size={12} /> {(meeting.warnings || []).length} warning{(meeting.warnings || []).length === 1 ? "" : "s"}
          </div>
          <div className="space-y-1 text-sm text-amber-900">
            {(meeting.warnings || []).map((w, i) => (
              <div key={`${w.type}-${w.employee_id || ""}-${w.session_id || ""}-${i}`} className="flex items-start gap-1">
                <AlertTriangle size={11} className="mt-1 shrink-0" /> {w.message}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Roster overview — single flat list of every employee in the meeting
          scope so admin can see "who's attending what, who covers them, who
          they cover" at a glance and act on any row. */}
      <RosterOverview
        meeting={meeting}
        empById={empById}
        isDraft={isDraft}
        onRemoveAttend={removeAttend}
        onExclude={excludeEmployee}
        onRestore={restoreEmployee}
      />

      {/* Session panels — one card per session, rows are paired attendee↔cover */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {(meeting.sessions || []).map((sess) => (
          <SessionCard
            key={sess.id}
            session={sess}
            data={bySession[sess.id]}
            empById={empById}
            isDraft={isDraft}
            onAttendDropIn={moveAttendTo}
            onUnassignableDropIn={(empId) => dropUnassignableToSession(empId, sess.id)}
            onCoverDrop={(payload, attendeeId, attendeeDept) => {
              if (payload.kind === "cover_pool") {
                dropPoolMemberToCover(payload.employee_id, sess.id, attendeeId, attendeeDept);
              } else if (payload.kind === "cover_slot") {
                moveCoverRow(payload.assignment_id, sess.id, attendeeId, attendeeDept);
              }
            }}
            onCoverRemove={removeCoverRow}
            onExclude={removeAttend}
            onToggleNoCover={toggleNoCover}
            onEditLocation={setSessionLocation}
          />
        ))}
      </div>

      {/* Cover Pool */}
      <Card
        className={`p-4 border-2 transition-colors ${
          poolDragHover ? "border-fuchsia-400 bg-fuchsia-50" : "border-slate-200"
        }`}
        onDragOver={(e) => {
          if (!isDraft) return;
          e.preventDefault();
          setPoolDragHover(true);
        }}
        onDragLeave={() => setPoolDragHover(false)}
        onDrop={(e) => {
          setPoolDragHover(false);
          if (!isDraft) return;
          try {
            const payload = JSON.parse(e.dataTransfer.getData("application/json") || "null");
            if (payload?.kind === "cover_slot") {
              removeCoverRow(payload.assignment_id);
            }
          } catch { /* noop */ }
        }}
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="text-[11px] uppercase tracking-widest font-black text-slate-500 flex items-center gap-1">
            <Users2 size={12} /> Available Coverers ({(meeting.cover_pool || []).length})
            <span className="ml-2 normal-case tracking-normal text-slate-400 font-normal italic">
              drag onto a cover slot to assign · drop a cover tile here to unassign
            </span>
          </div>
          {isDraft && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpenAddCoverer(true)}
              data-testid="btn-add-coverer"
            >
              <UserPlus size={13} className="mr-1" /> Add coverer
            </Button>
          )}
        </div>
        {(meeting.cover_pool || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic py-2">
            No manual coverers yet. Auto-assigned PDs still fill from availability;
            use "Add coverer" to slot in PDs / off employees who agreed by phone.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(meeting.cover_pool || []).map((p) => (
              <PoolTile
                key={p.employee_id}
                pool={p}
                sessions={meeting.sessions || []}
                assignments={meeting.assignments || []}
                isDraft={isDraft}
                onRemove={() => removeFromPool(p.employee_id)}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Unassignable list */}
      {(meeting.unassignable || []).length > 0 && (
        <Card className="p-4 border-2 border-slate-300">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[11px] uppercase tracking-widest font-black text-slate-500 flex items-center gap-1">
              <Users2 size={12} /> Needs self-signup ({(meeting.unassignable || []).length})
            </div>
            {meeting.status !== "cancelled" && (
              <Button size="sm" variant="outline" onClick={sendEmails} disabled={loading} data-testid="btn-send-emails">
                Email signup links
              </Button>
            )}
          </div>
          <div className="text-xs text-slate-500 mb-2 italic">
            These employees can't fit any session automatically — no shift overlap and no PD availability.
            {isDraft && " Drag a name onto a session card above to slot them in, or ✕ to mark them not required."}
          </div>
          <div className="space-y-1">
            {(meeting.unassignable || []).map((u) => {
              const emp = empById[u.employee_id];
              return (
                <div
                  key={u.employee_id}
                  className={`flex items-center gap-2 text-sm rounded px-1 py-0.5 ${
                    isDraft ? "cursor-grab active:cursor-grabbing hover:bg-slate-50" : ""
                  }`}
                  draggable={isDraft}
                  onDragStart={(e) => {
                    if (!isDraft) return;
                    e.dataTransfer.setData(
                      "application/json",
                      JSON.stringify({ kind: "unassignable", employee_id: u.employee_id }),
                    );
                  }}
                  data-testid={`unassign-${u.employee_id}`}
                >
                  <GripVertical size={12} className={isDraft ? "text-slate-300" : "invisible"} />
                  <span className="truncate flex-1">{emp?.name || u.employee_id}</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border bg-slate-100 border-slate-200 text-slate-700">
                    {u.reason === "no_availability" ? "PD — no availability"
                      : u.reason === "admin_removed" ? "removed by admin"
                      : u.reason === "restored" ? "restored"
                      : "no shift overlap"}
                  </span>
                  {isDraft && (
                    <button
                      onClick={() => excludeEmployee(u.employee_id, emp?.name)}
                      className="text-slate-400 hover:text-red-600 shrink-0"
                      title="Not required to attend"
                      data-testid={`unassign-exclude-${u.employee_id}`}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Not required to attend — excluded employees */}
      {(meeting.excluded || []).length > 0 && (
        <Card className="p-4 border-2 border-slate-200 bg-slate-50/60">
          <div className="text-[11px] uppercase tracking-widest font-black text-slate-500 flex items-center gap-1 mb-2">
            <Users2 size={12} /> Not required to attend ({(meeting.excluded || []).length})
          </div>
          <div className="text-xs text-slate-500 mb-2 italic">
            These employees won't be pulled into auto-assign or self-signup emails. Restore to bring them back on the roster.
          </div>
          <div className="flex flex-wrap gap-2">
            {(meeting.excluded || []).map((x) => (
              <div
                key={x.employee_id}
                className="flex items-center gap-2 text-sm rounded-md border border-slate-200 bg-white px-2 py-1"
                data-testid={`excluded-${x.employee_id}`}
              >
                <span className="font-medium truncate max-w-[180px]">{x.name}</span>
                {x.departments_trained?.length > 0 && (
                  <span className="text-[10px] text-slate-500 truncate max-w-[140px]" title={x.departments_trained.join(", ")}>
                    ({x.departments_trained.join(", ")})
                  </span>
                )}
                {x.availability_required && (
                  <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-amber-50 border-amber-300 text-amber-700 uppercase">PD</span>
                )}
                {(isDraft || meeting.status === "finalized") && (
                  <button
                    onClick={() => restoreEmployee(x.employee_id)}
                    className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-700 hover:text-fuchsia-900"
                    title="Restore to roster"
                    data-testid={`excluded-restore-${x.employee_id}`}
                  >
                    restore
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {openAddCoverer && (
        <AddCovererDialog
          meeting={meeting}
          emps={emps}
          onClose={() => setOpenAddCoverer(false)}
          onSaved={() => { setOpenAddCoverer(false); onChange(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Single flat list of every employee involved in the meeting. Aggregates
// attend rows, cover rows, unassignable list, and excluded list into one
// searchable table so the admin can see "who's attending what, who covers
// who" in one glance. Each row has a context-aware X / restore action.
function RosterOverview({ meeting, empById, isDraft, onRemoveAttend, onExclude, onRestore }) {
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState("");

  const sessionById = useMemo(() => {
    const m = {}; for (const s of meeting.sessions || []) m[s.id] = s; return m;
  }, [meeting.sessions]);

  // Build a lookup of "who covers X for session Y" and "who does X cover".
  const coverByAttendee = useMemo(() => {
    const m = {};   // key: employee_id → cover row (they're being covered by)
    for (const a of meeting.assignments || []) {
      if (a.role === "cover" && a.covers_employee_id) {
        m[a.covers_employee_id] = a;
      }
    }
    return m;
  }, [meeting.assignments]);

  // Every employee mentioned in this meeting → their status.
  const rows = useMemo(() => {
    const attendByEmp = {}, coverByEmp = {};
    for (const a of meeting.assignments || []) {
      if (a.role === "attend") attendByEmp[a.employee_id] = a;
      else if (a.role === "cover") coverByEmp[a.employee_id] = a;
    }
    const excludedIds = new Set((meeting.excluded || []).map(x => x.employee_id));
    const unassignableByEmp = {};
    for (const u of meeting.unassignable || []) unassignableByEmp[u.employee_id] = u;

    const ids = new Set([
      ...Object.keys(attendByEmp),
      ...Object.keys(coverByEmp),
      ...Object.keys(unassignableByEmp),
      ...excludedIds,
    ]);

    const out = [];
    for (const eid of ids) {
      const emp = empById[eid] || {};
      const attend = attendByEmp[eid];
      const cover = coverByEmp[eid];
      const excluded = excludedIds.has(eid);
      const unassignable = unassignableByEmp[eid];
      let status = "unknown";
      if (excluded) status = "excluded";
      else if (attend) status = "attending";
      else if (unassignable) status = "needs_signup";
      else if (cover) status = "covering_only";
      out.push({
        employee_id: eid,
        name: emp.name || eid,
        departments_trained: emp.departments_trained || [],
        availability_required: !!emp.availability_required,
        attend, cover,
        covered_by: coverByAttendee[eid] || null,
        unassignable,
        status,
      });
    }
    // Sort: attending first (by session start), then covering only, needs
    // signup, then excluded. Alphabetical within a group.
    const order = { attending: 0, covering_only: 1, needs_signup: 2, excluded: 3, unknown: 4 };
    out.sort((a, b) => (order[a.status] - order[b.status]) || a.name.localeCompare(b.name));
    return out;
  }, [meeting, empById, coverByAttendee]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(q));
  }, [rows, query]);

  const counts = useMemo(() => {
    const c = { attending: 0, covering_only: 0, needs_signup: 0, excluded: 0 };
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-[11px] uppercase tracking-widest font-black text-slate-500 flex items-center gap-1 hover:text-slate-800"
            data-testid="roster-overview-toggle"
          >
            <Users2 size={12} /> Roster overview ({rows.length})
            <span className="text-slate-400 font-normal">{collapsed ? "▸" : "▾"}</span>
          </button>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold">
            <span className="px-1.5 py-0.5 rounded border bg-emerald-50 border-emerald-300 text-emerald-800">
              {counts.attending} attending
            </span>
            {counts.covering_only > 0 && (
              <span className="px-1.5 py-0.5 rounded border bg-blue-50 border-blue-300 text-blue-800">
                {counts.covering_only} covering only
              </span>
            )}
            {counts.needs_signup > 0 && (
              <span className="px-1.5 py-0.5 rounded border bg-amber-50 border-amber-300 text-amber-800">
                {counts.needs_signup} needs signup
              </span>
            )}
            {counts.excluded > 0 && (
              <span className="px-1.5 py-0.5 rounded border bg-slate-100 border-slate-300 text-slate-600">
                {counts.excluded} not required
              </span>
            )}
          </div>
        </div>
        {!collapsed && (
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name…"
            className="max-w-xs h-8 text-sm"
            data-testid="roster-overview-filter"
          />
        )}
      </div>

      {!collapsed && (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                <tr className="text-[10px] uppercase tracking-widest font-black text-slate-500">
                  <th className="text-left px-2 py-1.5">Status</th>
                  <th className="text-left px-2 py-1.5">Employee</th>
                  <th className="text-left px-2 py-1.5">Attending</th>
                  <th className="text-left px-2 py-1.5">Covered by</th>
                  <th className="text-left px-2 py-1.5">Covers</th>
                  {isDraft && <th className="text-right px-2 py-1.5 w-16">Action</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const sess = r.attend ? sessionById[r.attend.session_id] : null;
                  const coverSess = r.cover ? sessionById[r.cover.session_id] : null;
                  const coveredBy = r.covered_by ? (empById[r.covered_by.employee_id]?.name || r.covered_by.employee_id) : null;
                  const coversName = r.cover?.covers_employee_id ? (empById[r.cover.covers_employee_id]?.name || r.cover.covers_employee_id) : null;
                  return (
                    <tr key={r.employee_id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50" data-testid={`roster-row-${r.employee_id}`}>
                      <td className="px-2 py-1.5">
                        <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${
                          r.status === "attending" ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                            : r.status === "covering_only" ? "bg-blue-50 border-blue-300 text-blue-800"
                            : r.status === "needs_signup" ? "bg-amber-50 border-amber-300 text-amber-800"
                            : r.status === "excluded" ? "bg-slate-100 border-slate-300 text-slate-600"
                            : "bg-slate-100 border-slate-200 text-slate-500"
                        }`}>
                          {r.status === "attending" ? "attending"
                            : r.status === "covering_only" ? "covering"
                            : r.status === "needs_signup" ? "needs signup"
                            : r.status === "excluded" ? "not required"
                            : "unknown"}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium truncate max-w-[160px]">{r.name}</span>
                          {r.availability_required && (
                            <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-amber-50 border-amber-300 text-amber-700 uppercase">PD</span>
                          )}
                          {r.attend?.attendee_dept && (
                            <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-slate-100 border-slate-200 text-slate-700 uppercase">
                              {r.attend.attendee_dept}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        {sess ? (
                          <div className="flex items-center gap-1.5 text-xs">
                            <span className="w-2 h-2 rounded" style={{ background: sess.color }} />
                            <span className="tabular-nums text-slate-600">
                              {format(parseISO(sess.date), "MMM d")} · {sess.start_time}–{sess.end_time}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400 italic text-xs">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {coveredBy ? (
                          <span className="text-xs text-slate-700">{coveredBy}</span>
                        ) : r.attend?.attendee_dept ? (
                          <span className="text-xs text-red-600 font-semibold">needs cover</span>
                        ) : (
                          <span className="text-slate-400 italic text-xs">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {coversName ? (
                          <div className="flex items-center gap-1 text-xs">
                            <span className="text-slate-700 truncate max-w-[140px]">{coversName}</span>
                            {coverSess && (
                              <span className="text-slate-400 tabular-nums">
                                ({coverSess.start_time})
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic text-xs">—</span>
                        )}
                      </td>
                      {isDraft && (
                        <td className="px-2 py-1.5 text-right">
                          {r.status === "attending" && (
                            <button
                              onClick={() => onRemoveAttend(r.employee_id, r.name)}
                              className="text-slate-400 hover:text-red-600"
                              title="Move to needs self-signup"
                              data-testid={`roster-remove-${r.employee_id}`}
                            >
                              <X size={14} />
                            </button>
                          )}
                          {r.status === "needs_signup" && (
                            <button
                              onClick={() => onExclude(r.employee_id, r.name)}
                              className="text-slate-400 hover:text-red-600"
                              title="Not required to attend"
                              data-testid={`roster-exclude-${r.employee_id}`}
                            >
                              <X size={14} />
                            </button>
                          )}
                          {r.status === "excluded" && (
                            <button
                              onClick={() => onRestore(r.employee_id)}
                              className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-700 hover:text-fuchsia-900"
                              title="Restore to needs self-signup"
                              data-testid={`roster-restore-${r.employee_id}`}
                            >
                              restore
                            </button>
                          )}
                          {r.status === "covering_only" && (
                            <span className="text-slate-300 text-xs" title="Drag their cover tile in the session cards below">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={isDraft ? 6 : 5} className="px-4 py-6 text-center text-sm text-slate-400 italic">
                      {rows.length === 0 ? "No employees yet — run auto-assign." : "No match for filter."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}


// -----------------------------------------------------------------------------
// One session card. Renders the attendee-row list: each row is `[attendee tile
// | cover tile]`. Cover tile is a drop target that accepts a pool member or a
// cover-slot drag from another row.
function SessionCard({ session, data, empById, isDraft, onAttendDropIn, onUnassignableDropIn, onCoverDrop, onCoverRemove, onExclude, onToggleNoCover, onEditLocation }) {
  const [dragHover, setDragHover] = useState(false);
  const attend = data?.attend || [];
  const coverByAttendee = data?.coverByAttendee || {};
  return (
    <Card
      className={`p-4 transition-colors ${dragHover ? "ring-2 ring-fuchsia-300" : ""}`}
      data-testid={`session-${session.id}`}
      onDragOver={(e) => {
        if (!isDraft) return;
        e.preventDefault();
        setDragHover(true);
      }}
      onDragLeave={() => setDragHover(false)}
      onDrop={(e) => {
        if (!isDraft) return;
        setDragHover(false);
        try {
          const payload = JSON.parse(e.dataTransfer.getData("application/json") || "null");
          if (payload?.kind === "attend") {
            onAttendDropIn(payload.assignment_id, session.id);
          } else if (payload?.kind === "unassignable") {
            onUnassignableDropIn(payload.employee_id);
          }
        } catch { /* noop */ }
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="w-3 h-3 rounded" style={{ background: session.color }} />
        <div className="font-bold text-slate-900">
          {format(parseISO(session.date), "EEE, MMM d")}
        </div>
        <div className="text-sm text-slate-600">
          {session.start_time}–{session.end_time}
        </div>
        <div className="ml-auto text-[11px] text-slate-500 tabular-nums">
          {attend.length} attending · {Object.keys(coverByAttendee).length} covered
        </div>
      </div>
      <div className="flex items-center gap-1 mb-3 text-[11px] text-slate-500">
        <MapPin size={11} className={session.location ? "text-fuchsia-600" : "text-slate-400"} />
        {isDraft ? (
          <button
            onClick={() => onEditLocation(session.id, session.location)}
            className={`hover:underline text-left ${session.location ? "text-slate-700 font-semibold" : "text-slate-400 italic"}`}
            data-testid={`session-location-${session.id}`}
          >
            {session.location || "TBD — click to set location"}
          </button>
        ) : (
          <span className={session.location ? "text-slate-700 font-semibold" : "text-slate-400 italic"}>
            {session.location || "TBD"}
          </span>
        )}
      </div>

      {attend.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-4 text-center border border-dashed border-slate-200 rounded">
          No attendees yet. Run auto-assign or drag an attendee here from another session.
        </div>
      ) : (
        <div className="space-y-1.5">
          {attend.map((a) => {
            const cover = coverByAttendee[a.employee_id];
            return (
              <PairRow
                key={a.id}
                attend={a}
                cover={cover}
                session={session}
                empById={empById}
                isDraft={isDraft}
                onCoverDrop={(payload) => onCoverDrop(payload, a.employee_id, a.attendee_dept)}
                onCoverRemove={onCoverRemove}
                onExclude={onExclude}
                onToggleNoCover={onToggleNoCover}
              />
            );
          })}
        </div>
      )}
    </Card>
  );
}

// -----------------------------------------------------------------------------
// One row = attendee tile + paired cover tile (drop target).
function PairRow({ attend, cover, session, empById, isDraft, onCoverDrop, onCoverRemove, onExclude, onToggleNoCover }) {
  const [dragHover, setDragHover] = useState(false);
  const emp = empById[attend.employee_id];
  const coverEmp = cover ? empById[cover.employee_id] : null;
  // "Needs cover" — attendee is on a work shift and no cover assigned AND
  // no admin override. PDs / manual attends (attendee_dept === null) don't
  // need cover; admin-flagged `no_cover_needed` also skips the red badge.
  const dept = attend.attendee_dept;
  const noCoverOverride = !!attend.no_cover_needed;
  const needsCover = !cover && !!dept && !noCoverOverride;
  // Training mismatch soft warning.
  const covererTrained = !dept || (coverEmp?.departments_trained || []).includes(dept);
  const trainingWarn = cover && dept && !covererTrained;
  const canToggleNoCover = !!dept && !cover;   // no point overriding when someone already covers

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
      {/* Attendee tile */}
      <div
        className={`flex items-center gap-2 text-sm rounded-md border px-2 py-1.5 bg-white ${
          isDraft ? "cursor-grab active:cursor-grabbing hover:bg-slate-50" : ""
        }`}
        draggable={isDraft}
        onDragStart={(e) => {
          if (!isDraft) return;
          e.dataTransfer.setData(
            "application/json",
            JSON.stringify({ kind: "attend", assignment_id: attend.id }),
          );
        }}
        data-testid={`attend-${attend.id}`}
      >
        <GripVertical size={12} className={isDraft ? "text-slate-300" : "invisible"} />
        <span className="truncate flex-1 font-medium">{emp?.name || attend.employee_id}</span>
        {attend.attendee_dept && (
          <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border bg-slate-100 border-slate-200 text-slate-700">
            {attend.attendee_dept}
          </span>
        )}
        {attend.mode === "pd_avail" && (
          <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border bg-amber-50 border-amber-300 text-amber-800">
            PD
          </span>
        )}
        {attend.mode === "manual_attend" && (
          <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border bg-purple-50 border-purple-300 text-purple-800">
            manual
          </span>
        )}
        {noCoverOverride && (
          <span
            className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border bg-blue-50 border-blue-300 text-blue-800 inline-flex items-center gap-1"
            title="Admin flagged: no coverage needed for this attendee"
          >
            <ShieldCheck size={10} /> no cover
          </span>
        )}
        {isDraft && canToggleNoCover && onToggleNoCover && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleNoCover(attend.id); }}
            className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border shrink-0 ${
              noCoverOverride
                ? "bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200"
                : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
            }`}
            title={noCoverOverride ? "Turn cover requirement back on" : "Mark as no cover needed (e.g. PFA, cashier)"}
            data-testid={`attend-nocover-${attend.id}`}
          >
            {noCoverOverride ? "✓ ok" : "no cover?"}
          </button>
        )}
        {isDraft && onExclude && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onExclude(attend.employee_id, emp?.name);
            }}
            className="text-slate-400 hover:text-red-600 shrink-0"
            title="Not required to attend — move to bottom"
            data-testid={`attend-exclude-${attend.id}`}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {/* Cover slot (drop target) */}
      <div
        className={`flex items-center gap-2 text-sm rounded-md border px-2 py-1.5 min-h-[36px] transition-colors ${
          dragHover
            ? "border-fuchsia-400 bg-fuchsia-50"
            : needsCover
              ? "border-red-300 bg-red-50/60 border-dashed"
              : trainingWarn
                ? "border-amber-300 bg-amber-50"
                : cover
                  ? "border-emerald-200 bg-emerald-50/40"
                  : "border-slate-200 bg-slate-50/40 border-dashed"
        }`}
        onDragOver={(e) => {
          if (!isDraft) return;
          e.preventDefault();
          setDragHover(true);
        }}
        onDragLeave={() => setDragHover(false)}
        onDrop={(e) => {
          setDragHover(false);
          if (!isDraft) return;
          e.stopPropagation();
          try {
            const payload = JSON.parse(e.dataTransfer.getData("application/json") || "null");
            if (payload?.kind === "cover_pool" || payload?.kind === "cover_slot") {
              onCoverDrop(payload);
            }
          } catch { /* noop */ }
        }}
        data-testid={`cover-slot-${session.id}-${attend.employee_id}`}
      >
        {cover ? (
          <div
            className="flex items-center gap-2 flex-1 min-w-0"
            draggable={isDraft}
            onDragStart={(e) => {
              if (!isDraft) return;
              e.dataTransfer.setData(
                "application/json",
                JSON.stringify({
                  kind: "cover_slot",
                  assignment_id: cover.id,
                  employee_id: cover.employee_id,
                  session_id: cover.session_id,
                }),
              );
            }}
          >
            <GripVertical size={12} className={isDraft ? "text-slate-300" : "invisible"} />
            <span className="text-[10px] uppercase tracking-widest font-black text-slate-500">covers →</span>
            <span className="truncate flex-1 font-medium">
              {coverEmp?.name || cover.employee_id}
            </span>
            {coverEmp?.departments_trained?.length > 0 && (
              <span className="text-[10px] text-slate-500 truncate max-w-[140px]" title={coverEmp.departments_trained.join(", ")}>
                ({coverEmp.departments_trained.join(", ")})
              </span>
            )}
            {trainingWarn && (
              <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border bg-amber-100 border-amber-300 text-amber-900 inline-flex items-center gap-1 shrink-0"
                    title={`Not trained in ${dept}`}>
                <AlertTriangle size={9} /> !{dept}
              </span>
            )}
            {isDraft && (
              <button
                onClick={() => onCoverRemove(cover.id)}
                className="text-slate-400 hover:text-red-600 shrink-0"
                title="Remove cover"
                data-testid={`cover-remove-${cover.id}`}
              >
                <X size={12} />
              </button>
            )}
          </div>
        ) : (
          <span className={`text-[11px] italic ${needsCover ? "text-red-700 font-semibold" : "text-slate-400"}`}>
            {needsCover
              ? `Needs cover · ${dept}`
              : noCoverOverride
                ? "no cover needed (admin)"
                : attend.mode === "pd_avail" || attend.mode === "manual_attend" || !attend.attendee_dept
                  ? "no cover needed"
                  : "drop a coverer here"}
          </span>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// One draggable tile in the pool. Auto-dims sessions that conflict with a
// scheduled shift; the tile itself remains draggable (drop handler on the
// cover slot is the authority). Shows a small chip listing sessions they're
// available for.
function PoolTile({ pool, sessions, assignments = [], isDraft, onRemove }) {
  const sessionById = useMemo(() => {
    const m = {}; for (const s of sessions) m[s.id] = s; return m;
  }, [sessions]);
  const conflicts = new Set(pool.conflicting_session_ids || []);
  // Sessions this pool member is currently assigned to cover.
  const assignedSessionIds = useMemo(() => {
    const set = new Set();
    for (const a of assignments) {
      if (a.role === "cover" && a.employee_id === pool.employee_id && a.session_id) {
        set.add(a.session_id);
      }
    }
    return set;
  }, [assignments, pool.employee_id]);
  const available = (pool.requested_session_ids || []).filter(
    (sid) => !conflicts.has(sid) && !assignedSessionIds.has(sid)
  );
  const allConflict = available.length === 0 && (pool.requested_session_ids || []).length > 0;
  const attendingSess = pool.attending_session_id ? sessionById[pool.attending_session_id] : null;

  const fmtSessLabel = (s) => {
    let dateLabel = "";
    try { dateLabel = format(parseISO(s.date), "EEE MMM d"); } catch { dateLabel = s.date || ""; }
    return { dateLabel, timeLabel: `${s.start_time}–${s.end_time}` };
  };

  return (
    <div
      className={`rounded-lg border px-2.5 py-1.5 bg-white text-sm shadow-sm transition-opacity ${
        allConflict ? "opacity-50" : ""
      } ${isDraft ? "cursor-grab active:cursor-grabbing hover:shadow-md" : ""}`}
      draggable={isDraft}
      onDragStart={(e) => {
        if (!isDraft) return;
        e.dataTransfer.setData(
          "application/json",
          JSON.stringify({ kind: "cover_pool", employee_id: pool.employee_id }),
        );
      }}
      data-testid={`pool-${pool.employee_id}`}
    >
      <div className="flex items-center gap-1.5">
        <GripVertical size={12} className={isDraft ? "text-slate-300" : "invisible"} />
        <span className="font-medium truncate">{pool.name}</span>
        {pool.availability_required && (
          <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-amber-50 border-amber-300 text-amber-700 uppercase">PD</span>
        )}
        {isDraft && (
          <button
            onClick={onRemove}
            className="text-slate-400 hover:text-red-600 ml-1"
            title="Remove from pool"
            data-testid={`pool-remove-${pool.employee_id}`}
          >
            <X size={12} />
          </button>
        )}
      </div>
      {pool.departments_trained?.length > 0 && (
        <div className="text-[10px] text-slate-500 mt-0.5 truncate max-w-[220px]" title={pool.departments_trained.join(", ")}>
          ({pool.departments_trained.join(", ")})
        </div>
      )}
      <div className="flex flex-col gap-1 mt-1.5">
        {(pool.requested_session_ids || []).map((sid) => {
          const s = sessionById[sid];
          if (!s) return null;
          const isConflict = conflicts.has(sid);
          const isAssigned = assignedSessionIds.has(sid);
          const { dateLabel, timeLabel } = fmtSessLabel(s);

          // Visual states, in priority order:
          // 1) Assigned → dimmed + line-through, "Assigned" tag
          // 2) Conflict → grey line-through
          // 3) Available → green
          const stateClasses = isAssigned
            ? "bg-slate-100 border-slate-300 text-slate-500 line-through"
            : isConflict
              ? "bg-slate-100 border-slate-300 text-slate-400 line-through"
              : "bg-emerald-50 border-emerald-300 text-emerald-800";

          const title = isAssigned
            ? `Already assigned to cover this session (${dateLabel} · ${timeLabel})`
            : isConflict
              ? `Conflicts with a scheduled shift (${dateLabel} · ${timeLabel})`
              : `Can cover this session (${dateLabel} · ${timeLabel})`;

          return (
            <span
              key={sid}
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border flex items-center gap-1 justify-between ${stateClasses}`}
              title={title}
              data-testid={`pool-${pool.employee_id}-session-${sid}${isAssigned ? "-assigned" : ""}`}
            >
              <span className="tabular-nums">
                {dateLabel} · {timeLabel}
              </span>
              {isAssigned && (
                <span className="text-[8px] uppercase tracking-widest font-black px-1 py-[1px] rounded bg-slate-200 text-slate-600 no-underline">
                  Assigned
                </span>
              )}
            </span>
          );
        })}
        {attendingSess && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-purple-50 border-purple-300 text-purple-800 tabular-nums">
            attends {(() => { const { dateLabel, timeLabel } = fmtSessLabel(attendingSess); return `${dateLabel} · ${timeLabel}`; })()}
          </span>
        )}
      </div>
      {pool.note && (
        <div className="text-[10px] text-slate-500 italic mt-1 truncate max-w-[220px]" title={pool.note}>
          "{pool.note}"
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function AddCovererDialog({ meeting, emps, onClose, onSaved }) {
  const [employeeId, setEmployeeId] = useState("");
  const [attendingSessionId, setAttendingSessionId] = useState("");
  const [requestedSessionIds, setRequestedSessionIds] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  const sessions = meeting.sessions || [];
  const chosenEmp = emps.find((e) => e.id === employeeId);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return emps.slice(0, 60);
    return emps.filter((e) => (e.name || "").toLowerCase().includes(q)).slice(0, 60);
  }, [emps, query]);

  const toggleSess = (sid) => {
    setRequestedSessionIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  };

  const submit = async () => {
    if (!employeeId) { toast.error("Pick an employee"); return; }
    if (requestedSessionIds.length === 0 && !attendingSessionId) {
      toast.error("Pick at least one session to cover, or a session to attend"); return;
    }
    setSaving(true);
    try {
      await api.post(`/meetings/${meeting.id}/cover-pool`, {
        employee_id: employeeId,
        requested_session_ids: requestedSessionIds,
        attending_session_id: attendingSessionId || null,
        note: note.trim() || null,
      });
      toast.success("Coverer added");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to add");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mt-8 mb-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-lg font-bold text-slate-900">Add coverer to pool</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Slot in a PD or off-benefitted employee who agreed to help outside of availability.
              After saving, drag their tile from the pool onto a cover slot.
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800" data-testid="add-coverer-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <Label>Employee</Label>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search employees…"
              className="mb-2"
              data-testid="add-coverer-search"
            />
            <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white">
              {filtered.map((e) => (
                <label
                  key={e.id}
                  className={`flex items-center gap-2 text-sm px-2 py-1 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${
                    employeeId === e.id ? "bg-fuchsia-50" : ""
                  }`}
                >
                  <input
                    type="radio"
                    checked={employeeId === e.id}
                    onChange={() => setEmployeeId(e.id)}
                    data-testid={`add-coverer-emp-${e.id}`}
                  />
                  <span className="truncate flex-1">{e.name}</span>
                  {e.availability_required && (
                    <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-amber-50 border-amber-300 text-amber-700 uppercase">PD</span>
                  )}
                  {e.departments_trained?.length > 0 && (
                    <span className="text-[10px] text-slate-500 truncate max-w-[180px]">
                      ({e.departments_trained.join(", ")})
                    </span>
                  )}
                </label>
              ))}
              {filtered.length === 0 && (
                <div className="p-3 text-sm text-slate-400 italic text-center">No match</div>
              )}
            </div>
          </div>

          {chosenEmp && (
            <>
              <div>
                <Label>Sessions this person can cover</Label>
                <div className="text-[11px] text-slate-500 mb-1">
                  Check every session they agreed to be available for. If a scheduled shift
                  ever conflicts, the pool tile will dim that session automatically.
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {sessions.map((s) => (
                    <label
                      key={s.id}
                      className="flex items-center gap-2 text-sm rounded-md border border-slate-200 px-2 py-1.5 cursor-pointer hover:bg-slate-50"
                    >
                      <Checkbox
                        checked={requestedSessionIds.includes(s.id)}
                        onCheckedChange={() => toggleSess(s.id)}
                        data-testid={`add-coverer-sess-${s.id}`}
                      />
                      <span className="w-2.5 h-2.5 rounded" style={{ background: s.color }} />
                      <span className="text-xs font-semibold">
                        {format(parseISO(s.date), "EEE MMM d")}
                      </span>
                      <span className="text-xs text-slate-500">
                        {s.start_time}–{s.end_time}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label>Also attending a session? (optional)</Label>
                <div className="text-[11px] text-slate-500 mb-1">
                  Benefitted employee who's off and wants to attend one session while covering
                  another — pick the session they'll attend here.
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setAttendingSessionId("")}
                    className={`text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md border transition-colors ${
                      attendingSessionId === ""
                        ? "bg-slate-800 border-slate-800 text-white"
                        : "bg-white border-slate-300 text-slate-700"
                    }`}
                  >
                    Not attending
                  </button>
                  {sessions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setAttendingSessionId(s.id)}
                      data-testid={`add-coverer-attend-${s.id}`}
                      className={`text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md border transition-colors flex items-center gap-1 ${
                        attendingSessionId === s.id
                          ? "bg-fuchsia-600 border-fuchsia-600 text-white"
                          : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <span className="w-2 h-2 rounded" style={{ background: s.color }} />
                      {format(parseISO(s.date), "MMM d")} · {s.start_time}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Note (optional)</Label>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Verbal OK by phone, only if ER is uncovered"
                  data-testid="add-coverer-note"
                />
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !employeeId}
            className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
            data-testid="add-coverer-save"
          >
            {saving ? "Saving…" : "Add to pool"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function NewMeetingDialog({ facilityId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [sessions, setSessions] = useState([
    { date: format(new Date(), "yyyy-MM-dd"), start_time: "08:00", end_time: "10:00", location: "" },
  ]);
  const [depts, setDepts] = useState([]);
  const [deptFilter, setDeptFilter] = useState([]);
  const [emps, setEmps] = useState([]);
  const [includeMode, setIncludeMode] = useState("all");
  const [selectedEmps, setSelectedEmps] = useState([]);
  const [excludeEmps, setExcludeEmps] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: r }, { data: c }] = await Promise.all([
          api.get(`/roster?facility_id=${facilityId}`),
          api.get(`/shift-codes?facility_id=${facilityId}`),
        ]);
        setEmps(r || []);
        setDepts([...new Set((c || []).map(x => x.department).filter(Boolean))].sort());
      } catch { /* soft-fail */ }
    })();
  }, [facilityId]);

  const addSession = () => {
    const last = sessions[sessions.length - 1];
    setSessions([...sessions, {
      date: last?.date || format(new Date(), "yyyy-MM-dd"),
      start_time: "10:00", end_time: "12:00",
      location: last?.location || "",
    }]);
  };
  const rmSession = (i) => setSessions(sessions.filter((_, idx) => idx !== i));
  const updateSess = (i, key, val) =>
    setSessions(sessions.map((s, idx) => idx === i ? { ...s, [key]: val } : s));

  const toggleDept = (d) => setDeptFilter(deptFilter.includes(d)
    ? deptFilter.filter(x => x !== d) : [...deptFilter, d]);
  const toggleEmp = (id) => setSelectedEmps(selectedEmps.includes(id)
    ? selectedEmps.filter(x => x !== id) : [...selectedEmps, id]);
  const toggleExclude = (id) => setExcludeEmps(excludeEmps.includes(id)
    ? excludeEmps.filter(x => x !== id) : [...excludeEmps, id]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Meeting name is required"); return; }
    if (sessions.length === 0) { toast.error("Add at least one session"); return; }
    for (const s of sessions) {
      if (!s.date || !s.start_time || !s.end_time) {
        toast.error("Each session needs a date + start + end"); return;
      }
      if (s.start_time >= s.end_time) {
        toast.error(`Session ${s.date}: start must be before end`); return;
      }
    }
    setSaving(true);
    try {
      const body = {
        facility_id: facilityId,
        name: name.trim(),
        description: desc.trim() || null,
        sessions: sessions.map((s) => ({
          date: s.date, start_time: s.start_time, end_time: s.end_time,
          location: (s.location || "").trim() || null,
        })),
        roster_scope: {
          department_ids: deptFilter.length > 0 ? deptFilter : null,
          employee_ids: (includeMode === "select" && selectedEmps.length > 0) ? selectedEmps : null,
          exclude_employee_ids: excludeEmps,
        },
      };
      const { data } = await api.post("/meetings", body);
      toast.success("Meeting created");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to create");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mt-8 mb-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-lg font-bold text-slate-900">New meeting</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Multi-session meeting for {facilityId}. All roster staff must attend one session.
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800" data-testid="new-meeting-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Meeting name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="e.g. Q4 All-Staff Sync"
                     data-testid="new-meeting-name" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={desc} onChange={(e) => setDesc(e.target.value)}
                     placeholder="Agenda summary…"
                     data-testid="new-meeting-desc" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Sessions</Label>
              <Button variant="outline" size="sm" onClick={addSession} data-testid="new-meeting-add-session">
                <Plus size={14} className="mr-1" /> Add session
              </Button>
            </div>
            <div className="space-y-2">
              {sessions.map((s, i) => (
                <div key={`sess-${i}`} className="flex flex-wrap items-end gap-2 bg-slate-50 rounded-lg p-2 border border-slate-200">
                  <div className="min-w-[130px] flex-1">
                    <Label className="text-[10px] uppercase tracking-widest">Date</Label>
                    <Input type="date" value={s.date}
                           onChange={(e) => updateSess(i, "date", e.target.value)}
                           data-testid={`new-sess-date-${i}`} />
                  </div>
                  <div className="w-28">
                    <Label className="text-[10px] uppercase tracking-widest">Start</Label>
                    <Input type="time" value={s.start_time}
                           onChange={(e) => updateSess(i, "start_time", e.target.value)}
                           data-testid={`new-sess-start-${i}`} />
                  </div>
                  <div className="w-28">
                    <Label className="text-[10px] uppercase tracking-widest">End</Label>
                    <Input type="time" value={s.end_time}
                           onChange={(e) => updateSess(i, "end_time", e.target.value)}
                           data-testid={`new-sess-end-${i}`} />
                  </div>
                  <div className="min-w-[160px] flex-1">
                    <Label className="text-[10px] uppercase tracking-widest">Location</Label>
                    <Input value={s.location || ""}
                           onChange={(e) => updateSess(i, "location", e.target.value)}
                           placeholder="TBD if blank"
                           data-testid={`new-sess-loc-${i}`} />
                  </div>
                  <Button variant="outline" size="icon" onClick={() => rmSession(i)}
                          disabled={sessions.length === 1}
                          data-testid={`new-sess-rm-${i}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Department filter (optional)</Label>
            <div className="text-[11px] text-slate-500 mb-1">
              Leave empty for facility-wide. Pick one or more to only pull staff trained in those departments.
            </div>
            <div className="flex flex-wrap gap-1.5">
              {depts.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDept(d)}
                  data-testid={`new-meeting-dept-${d}`}
                  className={`text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md border transition-colors ${
                    deptFilter.includes(d)
                      ? "bg-fuchsia-600 border-fuchsia-600 text-white"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {d}
                </button>
              ))}
              {depts.length === 0 && (
                <span className="text-xs text-slate-400 italic">No departments defined for this facility.</span>
              )}
            </div>
          </div>

          <div>
            <Label>Roster</Label>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setIncludeMode("all")}
                className={`text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md border transition-colors ${
                  includeMode === "all" ? "bg-fuchsia-600 border-fuchsia-600 text-white" : "bg-white border-slate-300 text-slate-700"
                }`}
              >
                Whole facility (with exclusions)
              </button>
              <button
                type="button"
                onClick={() => setIncludeMode("select")}
                className={`text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md border transition-colors ${
                  includeMode === "select" ? "bg-fuchsia-600 border-fuchsia-600 text-white" : "bg-white border-slate-300 text-slate-700"
                }`}
              >
                Pick specific employees
              </button>
            </div>
            <div className="max-h-52 overflow-y-auto rounded-md border border-slate-200 p-2 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {emps.map((e) => {
                  const included = includeMode === "select" && selectedEmps.includes(e.id);
                  const excluded = includeMode === "all" && excludeEmps.includes(e.id);
                  const on = includeMode === "select" ? included : !excluded;
                  return (
                    <label key={e.id} className="flex items-center gap-2 text-sm rounded px-1 py-0.5 cursor-pointer hover:bg-slate-50">
                      <Checkbox
                        checked={on}
                        onCheckedChange={() =>
                          includeMode === "select" ? toggleEmp(e.id) : toggleExclude(e.id)
                        }
                        data-testid={`new-meeting-emp-${e.id}`}
                      />
                      <span className="truncate">{e.name}</span>
                      {e.availability_required && (
                        <span className="ml-auto text-[9px] font-bold px-1 py-0.5 rounded border bg-amber-50 border-amber-300 text-amber-700 uppercase">PD</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="new-meeting-save">
            {saving ? "Creating…" : "Create meeting"}
          </Button>
        </div>
      </div>
    </div>
  );
}
